import { ForwardedRef, PropsWithChildren, ReactElement } from 'react';

import { classes } from '@/uiPrimitives/base/classes.helpers';

import {
	scrollInline,
	scrollInlineMeasure,
	scrollInlineContent,
	scrollInlineMasks,
	scrollInlineNegativePadding,
	notOverflowingCSS,
	scrollInlineContainer,
} from './ScrollInline.css';
import { translateInline } from '@/globals/translate/translateInline';
import { focusWrapperPseudo } from '@/uiPrimitives/base/focus.css';

export const scrollInlineTranslations = {
	'Scroll left in previous scroll area': translateInline({
		en: 'Scroll the figure left',
		nl: 'Scroll het figuur naar links',
	}),
	'Scroll right in previous scroll area': translateInline({
		en: 'Scroll the figure right',
		nl: 'Scroll het figuur naar rechts',
	}),
} as const;

export const ScrollInline = ({
	children,
	className,
	scrollAreaClassName,
	measureBeforeSlot,
	measureAfterSlot,
	mask,
	notOverflowing,
	scrollbarWidth,
	ref,
}: PropsWithChildren<{
	className?: string;
	scrollAreaClassName?: string;
	measureBeforeSlot?: ReactElement | false;
	measureAfterSlot?: ReactElement | false;
	mask?: keyof typeof scrollInlineMasks;
	notOverflowing?: boolean;
	scrollbarWidth: keyof typeof scrollInline;
	ref: ForwardedRef<HTMLDivElement>;
}>) => {
	return (
		<div className={classes(className, scrollInlineContainer)}>
			<div
				className={classes(
					focusWrapperPseudo,
					scrollInlineNegativePadding[scrollbarWidth],
				)}
			>
				<div
					ref={ref}
					className={classes(
						notOverflowing && notOverflowingCSS,
						scrollInline[scrollbarWidth],
						mask != null && scrollInlineMasks[mask],
						scrollAreaClassName,
					)}
					tabIndex={0}
				>
					<div className={classes(scrollInlineMeasure)}>
						{measureBeforeSlot}
						<div className={classes(scrollInlineContent)}>{children}</div>
						{measureAfterSlot}
					</div>
				</div>
			</div>
		</div>
	);
};

export const scrollLeft = (
	scrollAreaElement: HTMLDivElement,
	scrollRatio: number,
) => {
	const scrollDelta = scrollRatio * scrollAreaElement.clientWidth;

	// scrollBy has janky behaviour on iOS Safari
	scrollAreaElement.scrollTo({
		left: scrollAreaElement.scrollLeft - scrollDelta,
		behavior: 'smooth',
	});
};

export const scrollRight = (
	scrollAreaElement: HTMLDivElement,
	scrollRatio: number,
) => {
	const scrollDelta = scrollRatio * scrollAreaElement.clientWidth;

	// scrollBy has janky behaviour on iOS Safari
	scrollAreaElement.scrollTo({
		left: scrollAreaElement.scrollLeft + scrollDelta,
		behavior: 'smooth',
	});
};
